import { ArrowUpCircleIcon } from "@heroicons/react/24/outline";
import { DEFAULT_RESET_SLEEP, StreamCategory } from "@sablier/v2-constants";
import { framework } from "@sablier/v2-contracts";
import { guards, policy } from "@sablier/v2-machines";
import { BigNumber, _ } from "@sablier/v2-mixins";
import { Permit2, Stream } from "@sablier/v2-models";
import { vendors } from "@sablier/v2-utils";
import { tabs } from "~/client/constants";
import { isHostSafe } from "~/client/contexts/Web3";
import extensions, { linear } from "~/client/extensions";
import { toast } from "~/client/hooks/useToast";
import { contractor, peripheral } from "~/client/utils";
import type { useExpected } from "../hooks";
import type { useMachineForm } from "@sablier/v2-hooks";
import type { Translate } from "@sablier/v2-locales";
import type { ISigner } from "@sablier/v2-types";
import type { IForm } from "~/client/contexts/Form/Create/Group";
import type { useModalProxy, useModalTransaction } from "~/client/hooks/modals";
import type { IExtensionDependencies } from "~/client/types";
import helper from "./helper";
import { createGroup as wording } from "./wording";

export interface Payload {
  fields: IForm;
  signer: ISigner;
  proxy: string;
  api: {
    reset: () => void;
    setOpen: ReturnType<typeof useModalTransaction>["setOpen"];
    updateData: ReturnType<typeof useModalTransaction>["updateData"];
    doClose: ReturnType<typeof useModalTransaction>["doClose"];
    openProxyModal: ReturnType<typeof useModalProxy>["doOpen"];
    t: Translate;
    track: ReturnType<typeof useExpected>["track"];
  };
}

export interface Result {
  message?: string;
}

type Machine = Parameters<typeof useMachineForm<Payload, Result>>;

type onCheck = Parameters<Machine["0"]["onCheck"]>["0"];
type onProcess = Parameters<Machine["0"]["onProcess"]>["0"];
type onValidate = Parameters<Machine["0"]["onValidate"]>["0"];

export async function onCheck({ context }: onCheck): Promise<void> {
  const { streams, ...fields } = context.payload.fields;
  const { t } = context.payload.api;

  const flags = guards.validateFormFlags({
    t,
    isLoadingIncluded: true,
    isWarningIncluded: true,
    value: fields,
  });

  if (!_.isNilOrEmptyString(flags)) {
    throw new Error(flags);
  }

  const ids: (keyof typeof fields)[] = [
    "cancelability",
    "max",
    "shape",
    "token",
  ];

  const required = guards.validateFormRequired({
    t,
    required: ids,
    value: fields,
  });

  if (!_.isNilOrEmptyString(required)) {
    throw new Error(required);
  }

  if (_.isNil(streams.value) || !streams.value.length) {
    throw new Error(policy.stream.elements(t));
  }

  streams.value.forEach((stream) => {
    const { id: _index, extension, ...fields } = stream;
    const flags = guards.validateFormFlags({
      t,
      isLoadingIncluded: true,
      isWarningIncluded: true,
      value: fields,
    });
    if (!_.isNilOrEmptyString(flags)) {
      throw new Error(flags);
    }

    const ids: (keyof typeof fields)[] = ["address", "amount"];

    if (stream.duration.isActive) {
      ids.push("duration");
    } else {
      ids.push("start");
      ids.push("end");
    }
    const required = guards.validateFormRequired({
      t,
      required: ids,
      value: fields,
    });
    if (!_.isNilOrEmptyString(required)) {
      throw new Error(required);
    }

    const check = extensions.check({
      t,
      data: extension,
      isLoadingIncluded: true,
      isWarningIncluded: true,
      timing: stream.duration.isActive ? "duration" : "range",
    });

    if (!_.isNilOrEmptyString(check)) {
      throw new Error(check);
    }
  });
}

export async function onValidate({ context }: onValidate): Promise<undefined> {
  const { api, signer, proxy } = context.payload;
  const { streams, ...fields } = context.payload.fields;
  const { t } = api;

  api.setOpen(true, {
    status: "verify",
    title: wording.title(t),
    description: wording.confirm(t).description,
    isNotClosable: true,
  });

  try {
    await onCheck({ context });

    const totalAmount = streams.value
      .map((a) => _.toNumber(a.amount.value ?? 0))
      .reduce((a, b) => {
        return a + b;
      });

    const amountFlag = guards.validateAmount({
      t,
      context: "wallet",
      max: fields.max.value,
      min: "0",
      value: totalAmount.toString(),
    });

    if (!_.isNilOrEmptyString(amountFlag)) {
      throw new Error(amountFlag);
    }

    const sender = _.toAddress(signer.account!.address);
    const chainId = signer.chain!.id;

    const permit2Address = peripheral(chainId, "permit2").address;
    const registry = peripheral(chainId, "registry").address;

    const requested = streams.value
      .reduce(
        (sum, stream) => sum.plus(new BigNumber(stream.amount.value || 0)),
        new BigNumber(0),
      )
      .toString();

    await guards.validateInputs(
      t,
      [
        {
          purpose: "signer",
          options: {
            value: signer,
          },
        },
        {
          purpose: "token",
          options: {
            owner: sender,
            requested,
            spender: permit2Address,
            token: fields.token.value!,
          },
        },
        {
          purpose: "plugin",
          options: {
            provider: signer,
            name: "onStreamCanceled",
            proxy,
            registry,
          },
        },
      ],
      chainId,
      { toast },
    );
    /** Perform local validations for each stream. Use those that will not trigger a separate multicall. */
    for (let i = 0; i < streams.value.length; i++) {
      const stream = streams.value[i];
      const isAddress = _.isEthereumAddress(stream.address.value);

      await guards.validateInputs(
        t,
        [
          {
            purpose: "recipient",
            options: {
              value: stream.address.resolution?.address,
            },
          },
          ...(!stream.duration.isActive
            ? [
                {
                  purpose: "end" as const,
                  options: {
                    isExcess: true,
                    value: stream.end.value,
                  },
                },
              ]
            : []),
          ...(isAddress
            ? [
                {
                  purpose: "resolution" as const,
                  options: {
                    value: stream.address.value,
                    resolved: stream.address.resolution?.address,
                  },
                },
              ]
            : []),
        ],
        chainId,
        { toast },
      );
    }
  } catch (error) {
    vendors.crash.log(error);

    const description = _.toString(error).includes(policy.plugin.missing(t))
      ? wording.fail(t).plugin
      : wording.fail(t).description;

    const footer = _.toString(error).includes(policy.plugin.missing(t))
      ? [
          {
            onClick: () => {
              api.doClose();
              api.openProxyModal();
            },
            title: "Finish setup",
            accent: "warning" as const,
          },
        ]
      : undefined;

    api.updateData({
      status: "fail",
      description,
      footer,
      error: {
        message: _.toString(error),
        data: error,
      },
      isNotClosable: false,
    });
    throw error;
  }

  return undefined;
}

/**
 *  Machine state that actually triggers the transaction.
 *  It relies on defined, pre-validated values checked within the `onValidate` step.
 */

export async function onProcess({ context }: onProcess): Promise<void> {
  const { api, fields, signer, proxy } = context.payload;
  const { t } = api;

  try {
    api.updateData({
      status: "sign",
      description: wording.sign(t).description,
      isNotClosable: true,
    });

    const sender = _.toAddress(signer.account!.address);
    const chainId = signer.chain!.id;

    const permit2A = peripheral(chainId, "permit2").address;

    const token = fields.token.value!;
    const extension = fields.streams.value[0].extension;
    const timing = fields.streams.value[0].duration.isActive
      ? "duration"
      : "range";

    const streams = fields.streams.value.map((stream) => ({
      address: stream.address.value,
      amount: stream.amount.value,
      duration: stream.duration.value,
      end: stream.end.value,
      start: stream.start.value,
      extension: stream.extension,
    }));

    const dependencies = {
      chainId,
      sender: proxy,
      cancelability: fields.cancelability.value,
      token,
      proxy,
      timing,
      streams,
      signer,
    } satisfies IExtensionDependencies<"group">;

    const { amount } = extensions.precompute.group({
      dependencies,
      purpose: extension.purpose,
      timing,
    })!;

    const allowance = await Permit2.getAllowance({
      owner: sender,
      chainId,
      token,
      spender: proxy,
      permit2A,
    });

    const body = {
      amount,
      token,
      chainId,
      spender: proxy,
      nonce: allowance?.nonce ?? 0,
      signer,
      permit2A,
    };

    console.info("%c[pre-signature]", "color: mediumslateblue", {
      body,
      dependencies,
      safe: isHostSafe,
    });

    const permit2 = isHostSafe
      ? await Permit2.createSignatureSafe(body)
      : await Permit2.createSignature(body);

    const query = await extensions.process.group({
      dependencies,
      purpose: extension.purpose,
      timing,
      permit2,
    });
    const { label, labelShort } = wording.prepare(
      t,
      extension.purpose,
      dependencies,
    );

    if (_.isNil(query)) {
      throw new Error(policy.error.extension(t));
    }

    api.updateData({
      status: "confirm",
      description: wording.send(t, true, label).description,
      isNotClosable: true,
    });

    const config = await helper.configure({
      chainId,
      query: query.proxy,
      signer,
    });

    console.info("%c[pre-transaction]", "color: mediumslateblue", {
      query,
      config,
    });

    const transaction = await framework.write(config);

    api.updateData({
      status: "loading",
      description: wording.send(t, false, label).description,
      hash: !isHostSafe ? transaction.hash : undefined,
      isNotClosable: false,
    });

    api.track<"group">(transaction.hash, label, dependencies);

    const receipt = isHostSafe
      ? await framework.safeWait({ hash: transaction.hash })
      : await framework.wait({
          hash: transaction.hash,
          onReplaced: (replaced) => {
            api.updateData({
              hash: replaced.transaction.hash,
            });
          },
        });

    console.info("%c[post-transaction]", "color: mediumslateblue", {
      transaction,
      receipt,
    });

    if (receipt.status === "reverted") {
      throw new Error(policy.error.reverted(t));
    }

    const purpose = linear
      .map((i) => i.toLowerCase())
      .includes((extension.purpose || "")?.toLowerCase())
      ? StreamCategory.LOCKUP_LINEAR
      : StreamCategory.LOCKUP_DYNAMIC;

    const tokenIds = await helper.extractIds({
      receipt,
      purpose,
    });

    const contract = contractor(chainId, purpose, token?.address);
    const aliases = tokenIds
      ? tokenIds.map((tokenId) =>
          Stream.doGenerateAlias(contract.alias, chainId, tokenId),
        )
      : ["unknown"];

    const identifiers = tokenIds
      ? tokenIds.map((tokenId) =>
          Stream.doGenerateId(contract.address, chainId, tokenId),
        )
      : ["unknown"];

    await _.sleep(DEFAULT_RESET_SLEEP);

    api.updateData({
      status: "success",
      description: wording.success(t, labelShort, aliases.join(", "))
        .description,
      footer: [
        {
          to: tabs.dashboard.search.builder({
            i: identifiers,
            c: _.toString(chainId),
          }),
          title: "View streams",
        },
        {
          to: tabs.dashboard.sender.builder(),
          title: "Dashboard",
          right: ArrowUpCircleIcon,
        },
      ],
      hash: receipt.transactionHash,
      isNotClosable: true,
    });

    api.reset();
  } catch (error) {
    vendors.crash.log(error);
    api.updateData({
      status: "fail",
      description: wording.fail(t).description,
      error: {
        message: policy.error.message(t, error),
        data: error,
      },
      isNotClosable: false,
    });

    throw error;
  }
}
